import React, { useEffect, useState } from "react";
import { api } from "./api";
import { AllergyBanner, type AllergyPanelData } from "./AllergyBanner";
import { DoseForm, PaperBanner, usePaperMode, windowName, type DoseOptions, type PassMed } from "./Pass";

/**
 * Missed meds (build plan §9.1 #9): scheduled doses due but not logged, per
 * window and city, after the window closes ("so far" while it is open).
 * Departed students are left out. Each action is a NEW row for that dose:
 * back-fill (given late, with the reason), refused, held; an as-needed-only
 * student nobody asked gets "late PRN not needed". Entering from paper puts
 * the paper's time on the dose, and it leaves this list.
 */

interface Slot {
  personKey: string;
  lastName: string;
  firstName: string;
  city: string;
  medKey: string;
  windowCode: string;
  scheduledOn: string;
  phase?: "open" | "closed";
  med: PassMed;
}
interface MissedView extends DoseOptions {
  date: string;
  city: string;
  cities: string[];
  window: string;
  phases: { code: string; name: string; startTime: string; endTime: string; phase: string }[];
  slots: Slot[];
  notAsked: Slot[];
  allergy: Record<string, AllergyPanelData>;
}

const ACTIONS: [string, string][] = [["Given", "Back-fill (given late)"], ["Refused", "Refused"], ["Held", "Held"]];

function SlotRow({ s, v, paper, reload, prn }: { s: Slot; v: MissedView; paper: boolean; reload: () => void; prn?: boolean }) {
  const [action, setAction] = useState<string | null>(null);
  return (
    <li className="rounded-md border border-slate-200 p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="min-w-0"><span className="font-semibold">{s.lastName}, {s.firstName}</span> <span className="text-sm text-slate-600">· {s.city || "Not assigned"}</span></p>
        <span className="text-sm text-slate-600">{windowName(v, s.windowCode)}{s.phase === "open" ? " · so far (window open)" : ""}</span>
      </div>
      <p className="text-sm">{s.med.name} {s.med.strength} · {s.med.dose} · {s.med.route}{s.med.controlled ? " · controlled" : ""}</p>
      {v.canRecord && !action && (
        <div className="mt-2 flex flex-wrap gap-2">
          {prn ? (
            <button type="button" className="btn-secondary min-h-11" onClick={() => setAction("PrnNotNeeded")}>Late PRN not needed</button>
          ) : (
            ACTIONS.map(([k, l]) => <button key={k} type="button" className="btn-secondary min-h-11" onClick={() => setAction(k)}>{l}</button>)
          )}
        </div>
      )}
      {action && (
        <div className="mt-2">
          <AllergyBanner data={v.allergy[s.personKey]} />
          <DoseForm key={action} personKey={s.personKey} med={s.med} slot={{ windowCode: s.windowCode, scheduledOn: s.scheduledOn }} date={v.date} o={v} paper={paper} initial={action} onSaved={() => { setAction(null); reload(); }} onCancel={() => setAction(null)} />
        </div>
      )}
    </li>
  );
}

export function Missed(): React.ReactElement {
  const [paper, setPaper] = usePaperMode();
  const [date, setDate] = useState("");
  const [city, setCity] = useState("");
  const [windowCode, setWindowCode] = useState("");
  const [v, setV] = useState<MissedView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = () =>
    void api<{ missed: MissedView }>("GET", `/api/missed?${new URLSearchParams({ date, city, window: windowCode })}`).then((r) => (r.ok ? (setV(r.data.missed), setError(null)) : setError(r.data.error ?? "Could not load.")));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [date, city, windowCode]);
  return (
    <section>
      <p className="mb-3 text-sm"><a href="/pass" className="text-program underline">← Medication pass</a></p>
      <h2 className="mb-1 text-xl font-semibold">Missed meds</h2>
      <p className="mb-3 text-sm text-slate-600">Doses due but not recorded, after each window closes (and so far while it is open). Each action adds a new entry; nothing is changed or removed.</p>
      <PaperBanner on={paper} set={setPaper} />
      <div className="section-card flex flex-wrap items-end gap-3">
        <div>
          <label htmlFor="mm-d" className="field-label">Day</label>
          <input id="mm-d" type="date" className="field-input min-h-11" value={date || v?.date || ""} onChange={(e) => setDate(e.target.value)} />
        </div>
        <div>
          <label htmlFor="mm-w" className="field-label">Window</label>
          <select id="mm-w" className="field-input min-h-11" value={windowCode} onChange={(e) => setWindowCode(e.target.value)}>
            <option value="">Every window so far</option>
            {v?.phases.filter((w) => w.phase !== "before").map((w) => <option key={w.code} value={w.code}>{w.name} ({w.startTime}–{w.endTime}){w.phase === "open" ? " — open" : ""}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="mm-c" className="field-label">City</label>
          <select id="mm-c" className="field-input min-h-11" value={city} onChange={(e) => setCity(e.target.value)}>
            <option value="">All cities</option>
            {v?.cities.map((c) => <option key={c} value={c}>{c}</option>)}
            <option value="(not assigned)">Not assigned</option>
          </select>
        </div>
      </div>
      {error && <p className="field-error" role="alert">{error}</p>}
      {v && (
        <>
          <div className="section-card">
            <h3 className="mb-2 text-base font-semibold">Not recorded ({v.slots.length})</h3>
            {v.slots.length === 0 ? <p className="text-sm text-slate-600">Nothing missed.</p> : <ul className="space-y-2">{v.slots.map((s) => <SlotRow key={`${s.medKey}-${s.windowCode}`} s={s} v={v} paper={paper} reload={load} />)}</ul>}
          </div>
          {v.notAsked.length > 0 && (
            <div className="section-card">
              <h3 className="mb-1 text-base font-semibold">As needed: no answer recorded ({v.notAsked.length})</h3>
              <p className="mb-2 text-sm text-slate-600">Students with only as-needed medications the pass said to ask. Not counted as missed doses.</p>
              <ul className="space-y-2">{v.notAsked.map((s) => <SlotRow key={`${s.medKey}-${s.windowCode}`} s={s} v={v} paper={paper} reload={load} prn />)}</ul>
            </div>
          )}
        </>
      )}
    </section>
  );
}
